// Low-level scanner helpers shared across loop parsing modules.
// Example: splitTopLevelByComma("a, fn(1,2), b") -> ["a", " fn(1,2)", " b"].

// Split comma-separated list while preserving nested/quoted content.
export function splitTopLevelByComma(input: string): string[] {
  const parts: string[] = []
  let current = ''
  let quote: "'" | '"' | null = null
  let depthParen = 0
  let depthBrace = 0
  let depthBracket = 0

  // Walk source and split only on top-level commas.
  for (let i = 0; i < input.length; i += 1) {
    const char = input[i]
    const previousChar = i > 0 ? input[i - 1] : ''

    // Keep quoted chunks intact until matching quote closes.
    if (quote) {
      current += char
      if (char === quote && previousChar !== '\\') {
        quote = null
      } else {
        // Keep scanning quoted content.
      }
      continue
    }

    // Enter quoted mode for `'...'` or `"..."` segments.
    if (char === "'" || char === '"') {
      quote = char
      current += char
      continue
    } else {
      // Keep scanning non-quoted content.
    }

    // Track nesting so commas inside sub-structures are ignored.
    if (char === '(') {
      depthParen += 1
    } else if (char === ')' && depthParen > 0) {
      depthParen -= 1
    } else if (char === '{') {
      depthBrace += 1
    } else if (char === '}' && depthBrace > 0) {
      depthBrace -= 1
    } else if (char === '[') {
      depthBracket += 1
    } else if (char === ']' && depthBracket > 0) {
      depthBracket -= 1
    } else {
      // Keep depths unchanged for other characters.
    }

    // Split only when comma appears at top-level depth.
    if (char === ',' && depthParen === 0 && depthBrace === 0 && depthBracket === 0) {
      parts.push(current)
      current = ''
      continue
    } else {
      // Keep token content until split condition is met.
    }

    current += char
  }

  parts.push(current)
  // Return collected top-level parts.
  return parts
}

// Find matching closing token while ignoring nested and quoted text.
export function findMatchingPair(source: string, startIndex: number, openChar: string, closeChar: string): number {
  let quote: "'" | '"' | null = null
  let depth = 0

  // Scan forward and close when matching depth returns to zero.
  for (let index = startIndex; index < source.length; index += 1) {
    const char = source[index]
    const previousChar = index > 0 ? source[index - 1] : ''

    // Keep quoted content opaque to delimiter matching.
    if (quote) {
      if (char === quote && previousChar !== '\\') {
        quote = null
      } else {
        // Keep scanner in quote mode.
      }
      continue
    }

    // Enter quote mode when source opens a string literal.
    if (char === "'" || char === '"') {
      quote = char
      continue
    } else {
      // Continue delimiter tracking outside quotes.
    }

    if (char === openChar) {
      depth += 1
      continue
    } else if (char === closeChar) {
      depth -= 1
      // Return index when parser closes the original opening token.
      if (depth === 0) {
        return index
      } else {
        // Keep scanning nested closing tokens.
      }
    } else {
      // Ignore characters that are not delimiters.
    }
  }

  // Return -1 when matching pair is not found.
  return -1
}

// Skip whitespace and return index of the next non-space character.
export function skipWhitespace(source: string, startIndex: number): number {
  let index = startIndex
  // Move forward while current character is whitespace.
  while (index < source.length && isWhitespace(source[index])) {
    index += 1
  }

  // Return first non-whitespace index.
  return index
}

// Check whether character is a word-like token char.
export function isWordLike(char: string): boolean {
  if (char.length === 0) return false
  // Use charCodeAt for fast character classification without regex.
  const code = char.charCodeAt(0)
  return (
    (code >= 65 && code <= 90)   // A-Z
    || (code >= 97 && code <= 122) // a-z
    || (code >= 48 && code <= 57)  // 0-9
    || code === 95                 // _
    || code === 45                 // -
  )
}

// Check whether character is whitespace.
export function isWhitespace(char: string): boolean {
  // Use charCodeAt for fast whitespace detection without regex.
  const code = char.charCodeAt(0)
  return code === 32 || code === 9 || code === 10 || code === 13 // space, tab, LF, CR
}
